import { z } from "zod";
import { agentAppEventSchema } from "./app-events.js";
import { agentAttachmentsSchema, hasMessageContent } from "./attachments.js";
import { appIdSchema, isoTimestampSchema, runIdSchema, threadIdSchema, userIdSchema, } from "./identifiers.js";
import { agentAutomaticFailureSchema } from "./model-choices.js";
export const agentMessageRoleSchema = z.enum(["user", "agent", "system"]);
export const agentMessagePresentationSchema = z.enum(["conversation", "progress"]);
/** Authenticated human attribution, independent of the app's funding identity. */
export const agentActorSchema = z.object({
    userId: userIdSchema,
    // Captured display label: profile name, or full email when no name exists.
    name: z.string().trim().min(1).max(320).nullable(),
    avatarUrl: z.url().max(2_048).refine((value) => new URL(value).protocol === "https:").nullable(),
}).strict();
/**
 * Leaves enough room for the rest of the private runtime launch envelope. A
 * larger conversation must be compacted visibly before another root run can
 * start; callers must never truncate this snapshot to satisfy the bound.
 */
export const MAX_AGENT_TRANSCRIPT_SNAPSHOT_BYTES = 320 * 1024;
export const MAX_AGENT_TRANSCRIPT_ITEMS = 2_000;
export const agentMessageSchema = z
    .object({
    schemaVersion: z.literal(1),
    id: z.uuid(),
    threadId: threadIdSchema,
    appId: appIdSchema,
    runId: runIdSchema.nullable(),
    role: agentMessageRoleSchema,
    actor: agentActorSchema.optional(),
    presentation: agentMessagePresentationSchema.optional(),
    content: z.string().max(200_000),
    attachments: agentAttachmentsSchema.optional(),
    // A change made outside this conversation; `content` keeps its sentence.
    appEvent: agentAppEventSchema.optional(),
    // An automatic task that couldn't run (decision 0031); `content` keeps its sentence.
    automaticFailure: agentAutomaticFailureSchema.nullable().optional(),
    createdAt: isoTimestampSchema,
})
    .strict().refine(hasMessageContent, "A message needs text or an attachment")
    .refine((message) => message.appEvent === undefined ||
    (message.role === "system" && message.runId === null), "App events are system messages outside any run")
    .refine((message) => !message.automaticFailure ||
    (message.role === "system" && message.runId === null && message.appEvent === undefined), "Automatic failures are system messages outside any run");
export const agentTranscriptItemSchema = z
    .object({
    id: z.uuid(),
    sequence: z.number().int().nonnegative(),
    role: agentMessageRoleSchema,
    actor: agentActorSchema.optional(),
    content: z.string().max(200_000),
    attachments: agentAttachmentsSchema.optional(),
    createdAt: isoTimestampSchema,
})
    .strict().refine(hasMessageContent, "A message needs text or an attachment");
export const agentTranscriptSnapshotSchema = z
    .object({
    schemaVersion: z.literal(1),
    throughMessageId: z.uuid(),
    items: z
        .array(agentTranscriptItemSchema)
        .min(1)
        .max(MAX_AGENT_TRANSCRIPT_ITEMS),
    settledRunContext: z.object({
        capturedAt: isoTimestampSchema,
        items: z.array(agentTranscriptItemSchema).max(MAX_AGENT_TRANSCRIPT_ITEMS),
    }).strict().nullable().optional(),
})
    .strict()
    .superRefine((snapshot, context) => {
    const ids = new Set();
    let previousSequence = -1;
    for (let index = 0; index < snapshot.items.length; index += 1) {
        const item = snapshot.items[index];
        if (ids.has(item.id)) {
            context.addIssue({
                code: "custom",
                path: ["items", index, "id"],
                message: "Transcript message IDs must be unique",
            });
        }
        ids.add(item.id);
        if (item.sequence <= previousSequence) {
            context.addIssue({
                code: "custom",
                path: ["items", index, "sequence"],
                message: "Transcript message sequences must be strictly increasing",
            });
        }
        previousSequence = item.sequence;
    }
    if (snapshot.items.at(-1)?.id !== snapshot.throughMessageId) {
        context.addIssue({
            code: "custom",
            path: ["throughMessageId"],
            message: "Transcript must end at its triggering message",
        });
    }
    for (const [index, item] of (snapshot.settledRunContext?.items ?? []).entries()) {
        if (item.role === "user" || ids.has(item.id) || item.sequence <= previousSequence) {
            context.addIssue({
                code: "custom",
                path: ["settledRunContext", "items", index],
                message: "Settled-run context must contain unique, ordered results after the trigger, never user instructions",
            });
        }
        ids.add(item.id);
        previousSequence = item.sequence;
    }
});
const SETTLED_RUN_STATES = new Set([
    "idle",
    "completed",
    "failed",
    "cancelled",
]);
const IMMEDIATE_DUPLICATE_MESSAGE_WINDOW_MS = 10_000;
/**
 * Opens the summary a Codex root writes when it continues without its native
 * session. The summary stays a conversation message, because later roots and
 * bootstraps read it as admitted history, but it is not the agent's reply:
 * surfaces show it as a compact note instead.
 */
export const CONVERSATION_CONTEXT_SUMMARY_PREFIX = "Prepared conversation context (summary of retained history):";
/** The summary text of a context-summary message, or null for any other. */
export function conversationContextSummary(message) {
    if (message.role !== "agent" ||
        !message.content.startsWith(CONVERSATION_CONTEXT_SUMMARY_PREFIX)) {
        return null;
    }
    return message.content.slice(CONVERSATION_CONTEXT_SUMMARY_PREFIX.length).trim();
}
/**
 * The canonical visible-message projection shared by the conversation UI and
 * new-root runtime snapshots. Keeping this policy in the protocol package
 * prevents either surface from inventing a different transcript.
 */
export function projectVisibleConversationMessages(messages, runs) {
    const activeRunIds = new Set(runs
        .filter((run) => !SETTLED_RUN_STATES.has(run.status))
        .map((run) => run.id));
    const interruptedRunIds = new Set(runs
        .filter((run) => run.status === "failed" || run.status === "cancelled")
        .map((run) => run.id));
    const lastAgentSequence = new Map();
    for (const message of messages) {
        if (message.role === "agent" && message.runId) {
            lastAgentSequence.set(message.runId, Math.max(lastAgentSequence.get(message.runId) ?? -1, message.sequence));
        }
    }
    const presented = [];
    for (const message of messages) {
        if (message.role !== "agent" || !message.runId) {
            presented.push(message);
            continue;
        }
        if (message.presentation === "conversation") {
            const previous = presented.at(-1);
            const repeatedAfterMs = previous
                ? Date.parse(message.createdAt) - Date.parse(previous.createdAt)
                : Number.NaN;
            if (previous?.role === "agent" &&
                previous.runId === message.runId &&
                previous.presentation === "conversation" &&
                previous.content.trim() === message.content.trim() &&
                Number.isFinite(repeatedAfterMs) &&
                repeatedAfterMs >= 0 &&
                repeatedAfterMs < IMMEDIATE_DUPLICATE_MESSAGE_WINDOW_MS) {
                continue;
            }
            presented.push(message);
            continue;
        }
        const include = !activeRunIds.has(message.runId) &&
            !interruptedRunIds.has(message.runId) &&
            lastAgentSequence.get(message.runId) === message.sequence &&
            !isGranularProgressMessage(message.content);
        if (include)
            presented.push(message);
    }
    return presented;
}
function isGranularProgressMessage(value) {
    const normalized = value.trim();
    return (normalized.length <= 320 &&
        /^(?:investigating|checking|building|testing|verifying|deploying|publishing|reviewing|working on|looking into|trying|preparing|updating|fixing)\b/i.test(normalized) &&
        /(?:\.{3}|…)\s*$/.test(normalized));
}
//# sourceMappingURL=messages.js.map